'use client'

import React, { forwardRef, useMemo, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { cn } from '@/lib/utils'
import type { ButtonProps } from '@components/button'
import { Button } from '@components/button'
import * as Popover from '@radix-ui/react-popover'
import { Input } from '@components/input'

interface ColorPickerProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  hexPreviewClassName?: string
  className?: string
}

function useForwardedRef<T>(ref: React.ForwardedRef<T>): React.RefObject<T> {
  const innerRef = React.useRef<T>(null)

  React.useEffect(() => {
    if (!ref) return
    if (typeof ref === 'function') {
      ref(innerRef.current)
    } else {
      ref.current = innerRef.current
    }
  })

  return innerRef
}

const ColorPicker = forwardRef<HTMLInputElement, Omit<ButtonProps, 'value' | 'onChange' | 'onBlur'> & ColorPickerProps>(
  ({ disabled, value, onChange, onBlur, name, className, hexPreviewClassName, ...props }, forwardedRef) => {
    const ref = useForwardedRef(forwardedRef)
    const [open, setOpen] = useState(false)

    const parsedValue = useMemo(() => {
      return value || '#FFFFFF'
    }, [value])

    return (
      <div className="gap-x-xs flex items-center">
        <Popover.Root onOpenChange={setOpen} open={open}>
          <Popover.Trigger asChild disabled={disabled} onBlur={onBlur}>
            <Button
              {...props}
              className={hexPreviewClassName}
              name={name}
              onClick={() => {
                setOpen(true)
              }}
              style={{
                backgroundColor: parsedValue
              }}
            ></Button>
          </Popover.Trigger>
          <Popover.Content className="bg-surface-container-inverted p-xs z-50 w-full translate-x-[16px] shadow-[2px_2px_6px_0px_rgba(0,0,0,0.20)]">
            <HexColorPicker color={parsedValue} onChange={onChange} className="custom-color-picker" />
            <Input
              maxLength={7}
              onChange={(e) => {
                onChange(e?.currentTarget?.value)
              }}
              className={cn(className, 'mt-xs')}
              ref={ref}
              value={parsedValue}
            />
          </Popover.Content>
        </Popover.Root>
        <Input
          maxLength={7}
          onChange={(e) => {
            onChange(e?.currentTarget?.value)
          }}
          className={className}
          ref={ref}
          value={parsedValue}
        />
      </div>
    )
  }
)
ColorPicker.displayName = 'ColorPicker'

export { ColorPicker }
